import React, { useMemo } from 'react';
import { Box, Dialog } from '@primer/components';
import { useSelector } from 'react-redux';
import { getDynamicBorder, getEventThemeCSSVars } from 'utils/appDesigner';
import { Button } from 'uikit/buttons/Button/Button';
import CustomPopup from './CustomPopup/CustomPopup';
import './Popup.scss';

interface Props {
    headerTitle?: string;
    isOpen: boolean;
    handleClose: () => void;
    primaryButtonText?: string;
    primaryButtonOnClick?: () => void;
    secondaryButtonText?: string;
    isPrimaryButtonDisabled?: boolean;
    isHideSecondaryButton?: boolean;
    extraButton?: Element;
    isFullWidth?: boolean;
    zIndex?: number;
    isApplyEventThemed?: boolean;
    /**
     * Opt-in: apply Redux event themeColors even when body is not tagged
     * (e.g. CMS Directory hub preview). Does not change default Popup theming.
     */
    useEventTheme?: boolean;
    /**
     * When true with useEventTheme, draw a dynamic border from the modal
     * background (getDynamicBorder). Ignored unless useEventTheme is on.
     */
    showEventThemeBorder?: boolean;
    dialogClassName?: string;
}

const Popup: React.FC<Props> = (props) => {
    const {
        headerTitle,
        isOpen,
        handleClose,
        primaryButtonText,
        primaryButtonOnClick,
        secondaryButtonText = 'Cancel',
        isPrimaryButtonDisabled,
        children,
        isHideSecondaryButton = false,
        extraButton = <></>,
        isFullWidth = false,
        zIndex,
        isApplyEventThemed = true,
        useEventTheme = false,
        showEventThemeBorder = false,
        dialogClassName,
    } = props;

    const { themeColors, typography } = useSelector(
        (state: any) => state.eventWrapper || {}
    );

    const isEventThemed =
        (useEventTheme && !!themeColors?.backgroundColor) ||
        (isApplyEventThemed &&
            document.body.getAttribute('data-event-themed') === 'true');

    const sectionTitleStyle = useMemo(() => {
        if (!isEventThemed || !typography?.typographyItems) return null;
        return typography.typographyItems.find(
            (item: any) => item.id === 'sectionTitle'
        );
    }, [isEventThemed, typography]);

    const buttonsStyle = useMemo(() => {
        if (!isEventThemed || !typography?.typographyItems) return null;
        return typography.typographyItems.find(
            (item: any) => item.id === 'buttons'
        );
    }, [isEventThemed, typography]);

    const bgBorderColor = useMemo(
        () =>
            isEventThemed
                ? getDynamicBorder({
                      backgroundColor: themeColors?.backgroundColor,
                      adjustAmount: null,
                      skipBorderValidation: true,
                      returnColorOnly: true,
                  })
                : null,
        [isEventThemed, themeColors?.backgroundColor]
    );

    const popupContainerStyles = useMemo(() => {
        if (!isEventThemed || !themeColors) return {};
        // Portaled Dialog sits outside CMS preview wrappers that already set
        // eventThemeCSSVars — without these, `.event-themed-fields .button.primary`
        // resolves empty --event-accent-color and Message/action buttons look wrong.
        const styles: React.CSSProperties = {
            ...getEventThemeCSSVars(themeColors),
            backgroundColor: themeColors.backgroundColor,
            color: themeColors.textAndIconOnBackgroundColor,
        };
        // Consistent width for event-themed directory (and similar) modals
        if (useEventTheme) {
            styles.minWidth = 520;
            styles.width = 560;
            styles.maxWidth = 'calc(100vw - 32px)';
            styles.boxSizing = 'border-box';
        }
        return styles;
    }, [isEventThemed, themeColors, useEventTheme]);

    const headerStyles = useMemo(() => {
        if (!isEventThemed || !themeColors) return {};
        return {
            backgroundColor: themeColors.secondaryCardsPanelsColor,
            color: themeColors.textAndIconOnSecondaryCardsPanelsColor,
            borderBottomColor: bgBorderColor || 'transparent',
            fontSize: `${sectionTitleStyle?.fontSize || 18}px`,
            fontWeight: sectionTitleStyle?.fontWeight || '500',
        };
    }, [isEventThemed, themeColors, bgBorderColor, sectionTitleStyle]);

    const buttonContainerStyles = useMemo(() => {
        if (!isEventThemed || !themeColors) return {};
        return {
            backgroundColor: themeColors.secondaryCardsPanelsColor,
            borderTopColor: bgBorderColor || 'transparent',
        };
    }, [isEventThemed, themeColors, bgBorderColor]);

    const primaryButtonStyles = useMemo(() => {
        if (!isEventThemed || !themeColors) return {};
        return {
            backgroundColor: themeColors.accentColor,
            color: themeColors.textAndIconOnAccentColor,
            fontSize: `${buttonsStyle?.fontSize || 16}px`,
            fontWeight: buttonsStyle?.fontWeight || '500',
        };
    }, [isEventThemed, themeColors, buttonsStyle]);

    const secondaryButtonStyles = useMemo(() => {
        if (!isEventThemed || !themeColors) return {};
        return {
            color: themeColors.accentColor,
            borderColor: themeColors.accentColor,
            fontSize: `${buttonsStyle?.fontSize || 16}px`,
            fontWeight: buttonsStyle?.fontWeight || '500',
        };
    }, [isEventThemed, themeColors, buttonsStyle]);

    return (
        <CustomPopup
            open={isOpen}
            isFullWidth={isFullWidth}
            zIndex={zIndex}
            isApplyEventThemed={isApplyEventThemed}
            useEventTheme={useEventTheme}
            showEventThemeBorder={showEventThemeBorder}
            dialogClassName={dialogClassName}
            paperMinWidth={useEventTheme ? 520 : undefined}
            paperWidth={useEventTheme ? 560 : undefined}
        >
            <Box
                className={`popup-contianer ${
                    isEventThemed ? 'event-themed event-themed-fields' : ''
                }`}
                style={popupContainerStyles}
            >
                {headerTitle && (
                    <Dialog.Header id="header-id" style={headerStyles}>
                        {isEventThemed ? (
                            <div className="header-title">{headerTitle}</div>
                        ) : (
                            headerTitle
                        )}
                    </Dialog.Header>
                )}
                <Box>{children}</Box>
                {isHideSecondaryButton && !primaryButtonText ? (
                    <></>
                ) : (
                    <Box
                        className="button-container"
                        style={buttonContainerStyles}
                    >
                        <Button
                            variant="secondary"
                            buttonClass={`${
                                isHideSecondaryButton ? 'button-hide' : ''
                            }`}
                            onClick={handleClose}
                            style={secondaryButtonStyles}
                        >
                            {secondaryButtonText}
                        </Button>
                        <Box display="flex">
                            {extraButton && (
                                <Box display="flex" paddingRight="15px">
                                    {extraButton}{' '}
                                </Box>
                            )}
                            {primaryButtonText && (
                                <Button
                                    variant="primary"
                                    buttonClass={`${
                                        isPrimaryButtonDisabled
                                            ? 'disabled'
                                            : ''
                                    }`}
                                    onClick={primaryButtonOnClick}
                                    disabled={isPrimaryButtonDisabled}
                                    isAllowDisable
                                    style={primaryButtonStyles}
                                >
                                    {primaryButtonText}
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        </CustomPopup>
    );
};

export default Popup;
